/**
 * Demo containment list (ADR 0006 dec. 5 · issue 03).
 *
 * The public demo is AIR®, single-tenant (ADR 0001), so a handful of surfaces
 * are out of scope. Containment is **behavioral, not structural** — every route
 * stays in the bundle; these paths redirect to `/home` under `isDemoPresentation`
 * (see the guard in `src/routes/_dashboard.tsx` `beforeLoad`) and their nav items
 * are stripped from the demo shell (`app-sidebar.tsx`). In the demo `/home` itself
 * redirects straight on to the overview — it has no onboarding to show (ticket 03).
 *
 * This is the single source of truth for the exclusion set: the route guard,
 * the nav gating, and the Playwright smoke (issue 06) all read from here so they
 * can never drift. Add a path prefix here and all three follow.
 *
 * ONE containment redirect lives outside this set, because it is data-dependent
 * rather than path-dependent and so cannot be expressed as a path: an
 * `/integrations/cloud/$id` that the demo world doesn't connect bounces to the list
 * from that route's own loader (ticket 04). If you are auditing demo redirects,
 * that is the one you won't find here.
 *
 * The demo has THREE containment mechanisms; only the first is expressed here:
 * route redirect (this set), nav/affordance hiding (`affordances.ts`,
 * `DemoHidden`), and CONSTRAINED RENDER — a surface that stays and renders a
 * demo-specific body. `/report/air` is the constrained one: no PDF exists to
 * download, so it renders a live-metric preview behind a book-a-demo gate
 * (`features/air/components/demo-report-preview.tsx`, ADR 0015 dec. 5).
 *
 * NOTE: "Ask Noah" (the AI panel) used to be demo-excluded here in spirit —
 * ADR 0015 dec. 4 reversed that. It is now *constrained*, not hidden: the panel
 * opens on Search and Case details with a locked composer and fixed prompt
 * chips. `use-can-use-ai.ts` carries no demo gate any more.
 */
export const DEMO_EXCLUDED_ROUTES = [
	// Multi-tenant / subtenant management — demo is single-tenant.
	'/multi-tenant',
	// Graylog-parity surfaces — separate SIEM, not the go-forward platform (ADR 0001).
	'/graylog',
	// The Graylog report view specifically ( /report itself stays included ).
	'/report/graylog',
	// Every administration surface (profile, members, invitations, API keys,
	// SSO, notification recipients + preferences). One prefix instead of ~20
	// button gates — and it's the only way to kill the delete-account flow,
	// which fires with no confirm dialog and lands the visitor on /logout
	// (ticket 03 · affordance inventory §13).
	'/settings',
] as const

/**
 * Demo-excluded routes whose path carries a param, so the prefix list above
 * can't express them: the parent list surface stays included and only the
 * editor child is excluded. Anchored, single-segment patterns — they must not
 * swallow deeper descendants that don't exist today but might.
 *
 * The first two are policy/automation **editors** (create + edit share the route
 * pair); `/new` is matched by the same pattern as an id, which is exactly right.
 *
 * The third is connector setup (ticket 04). `/integrations/new` — the picker —
 * stays: it is the only entry point to the coverage grid, which is where the
 * demo makes its integration-breadth argument. Its children are the setup flow:
 * `new/$id` is the credential form, and `new/callback` is the Microsoft OAuth
 * return, dead-by-design in demo (the consent-URL mock returns `about:blank`,
 * so the popup never posts back). One pattern covers both.
 */
const DEMO_EXCLUDED_ROUTE_PATTERNS = [
	/^\/air\/policy-management\/[^/]+$/,
	/^\/air\/automation-settings\/[^/]+$/,
	/^\/integrations\/new\/[^/]+$/,
] as const

/**
 * True when `pathname` is a demo-excluded route — by prefix or by pattern.
 *
 * The prefix arm matches the exact path or a `/`-delimited descendant, so
 * `/graylog` catches `/graylog/alerts` but a hypothetical `/graylog-foo` is
 * left alone, and `/report/graylog` never over-matches the included `/report`.
 * The pattern arm then catches the param editor routes whose parent list
 * surface must stay reachable.
 */
export function isDemoExcludedPath(pathname: string): boolean {
	return (
		DEMO_EXCLUDED_ROUTES.some(
			(route) => pathname === route || pathname.startsWith(`${route}/`),
		) || DEMO_EXCLUDED_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
	)
}
